import {type FormEvent, type ReactNode, useState} from "react";

import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
  ArrowLeftIcon,
  CheckIcon,
  CopyIcon,
  Loader2Icon,
  MoreHorizontalIcon,
  PlusIcon,
  ShieldOffIcon,
  TriangleAlertIcon,
} from "lucide-react";
import {Link, useNavigate, useParams} from "react-router-dom";
import {toast} from "sonner";

import {
  type ApiKeyView,
  type IssueKeyOutputBody,
  adminOrgApiKeysIssue,
  adminOrgApiKeysList,
  adminOrgApiKeysRevoke,
  adminOrgsGet,
} from "@/sdk";

import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {Card, CardContent} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {formatRelative, formatTimestamp} from "@/lib/format";

export const AdminOrgApiKeys = () => {
  const {id} = useParams<{id: string}>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const orgId = id ?? "";

  const [issueOpen, setIssueOpen] = useState(false);
  const [issueName, setIssueName] = useState("");
  const [issuedKey, setIssuedKey] = useState<IssueKeyOutputBody | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyView | null>(null);

  const QK = ["admin", "org-api-keys", orgId] as const;

  const orgQuery = useQuery({
    queryKey: ["admin", "orgs", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const {data, error} = await adminOrgsGet({path: {id: orgId}});
      if (error) throw error;
      return data;
    },
  });

  const keysQuery = useQuery({
    queryKey: QK,
    enabled: !!orgId,
    queryFn: async () => {
      const {data, error} = await adminOrgApiKeysList({path: {id: orgId}});
      if (error) throw error;
      return data?.items ?? [];
    },
  });

  const invalidate = () => qc.invalidateQueries({queryKey: QK});

  const issueM = useMutation({
    mutationFn: () =>
      adminOrgApiKeysIssue({
        path: {id: orgId},
        body: {name: issueName.trim()},
      }),
    onSuccess: ({data, error}) => {
      if (error || !data) {
        toast.error(extractError(error));
        return;
      }
      setIssuedKey(data);
      setIssueOpen(false);
      setIssueName("");
      invalidate();
    },
  });

  const revokeM = useMutation({
    mutationFn: (keyId: string) =>
      adminOrgApiKeysRevoke({path: {id: orgId, key_id: keyId}}),
    onSuccess: ({error}) => {
      if (error) {
        toast.error(extractError(error));
        return;
      }
      toast.success("API key revoked");
      setRevokeTarget(null);
      invalidate();
    },
  });

  const handleIssueSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!issueName.trim()) return;
    issueM.mutate();
  };

  const keys = keysQuery.data ?? [];
  const org = orgQuery.data;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="space-y-1">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 gap-1 text-muted-foreground"
            onClick={() => navigate("/admin/orgs")}
          >
            <ArrowLeftIcon /> Organizations
          </Button>
          <h1 className="font-heading text-2xl font-semibold">
            API keys{org ? ` — ${org.name}` : ""}
          </h1>
          <p className="text-sm text-muted-foreground">
            Org-scoped keys authenticate this tenant's automation against the Eyrie API.
          </p>
        </div>
        <Button onClick={() => setIssueOpen(true)} disabled={!orgId}>
          <PlusIcon /> Issue new key
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {keysQuery.isLoading ? (
            <div className="flex items-center justify-center p-12 text-muted-foreground">
              <Loader2Icon className="size-5 animate-spin" />
            </div>
          ) : keysQuery.isError ? (
            <div className="p-6 text-sm text-destructive">Failed to load API keys.</div>
          ) : keys.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">
              No API keys issued yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Prefix</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last used</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keys.map((k) => {
                  const revoked = !!k.revoked_at;
                  return (
                    <TableRow key={k.id}>
                      <TableCell className="font-medium">{k.name}</TableCell>
                      <TableCell className="font-mono text-xs">{k.prefix}…</TableCell>
                      <TableCell>
                        {revoked ? (
                          <Badge variant="muted">revoked</Badge>
                        ) : (
                          <Badge variant="success">active</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatRelative(k.created_at)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatRelative(k.last_used_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon-sm">
                              <MoreHorizontalIcon />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              variant="destructive"
                              disabled={revoked}
                              onSelect={() => setRevokeTarget(k)}
                            >
                              <ShieldOffIcon /> Revoke
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Need to find an organization?{" "}
        <Link to="/admin/orgs" className="underline-offset-4 hover:underline">
          Back to all organizations
        </Link>
        .
      </p>

      <Dialog open={issueOpen} onOpenChange={(o) => !o && setIssueOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Issue new API key</DialogTitle>
            <DialogDescription>
              Give the key a human-readable name (e.g. "CI deploy" or "Terraform").
              The plaintext token is shown ONCE — capture it immediately.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleIssueSubmit} className="space-y-4">
            <Field id="key_name" label="Name">
              <Input
                id="key_name"
                required
                placeholder="CI deploy"
                value={issueName}
                onChange={(e) => setIssueName(e.target.value)}
              />
            </Field>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIssueOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={issueM.isPending || !issueName.trim()}>
                {issueM.isPending ? "Issuing…" : "Issue key"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <PlaintextKeyDialog
        issued={issuedKey}
        onClose={() => setIssuedKey(null)}
      />

      <Dialog open={revokeTarget !== null} onOpenChange={(o) => !o && setRevokeTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke API key?</DialogTitle>
            <DialogDescription>
              Revokes <span className="font-mono">{revokeTarget?.name}</span>. Any clients
              still using this key will start receiving 401s immediately. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={revokeM.isPending}
              onClick={() => revokeTarget && revokeM.mutate(revokeTarget.id)}
            >
              {revokeM.isPending ? "Revoking…" : "Revoke"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export function PlaintextKeyDialog({
  issued,
  onClose,
}: {
  issued: IssueKeyOutputBody | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.token);
      setCopied(true);
      toast.success("Token copied to clipboard");
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — select and copy manually");
    }
  };

  const handleClose = () => {
    setCopied(false);
    onClose();
  };

  return (
    <Dialog open={issued !== null} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent
        onInteractOutside={(e) => {
          // Force the operator to acknowledge — don't dismiss on outside click.
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Save this token now</DialogTitle>
          <DialogDescription>
            This is the ONLY time the plaintext token will be shown. If you navigate away
            without copying it, the only recovery is to revoke this key and issue a new one.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
            <TriangleAlertIcon className="mt-0.5 size-4 shrink-0" />
            <span>
              The server only stored a hash of this token. Eyrie cannot retrieve it later
              under any circumstances.
            </span>
          </div>

          {issued ? (
            <div className="space-y-2">
              <Label>Plaintext token</Label>
              <div className="flex items-stretch gap-2">
                <code className="flex-1 break-all rounded-md border bg-muted px-3 py-2 font-mono text-xs">
                  {issued.token}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  className="shrink-0"
                >
                  {copied ? <CheckIcon /> : <CopyIcon />}
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
              <dl className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <dt>Name</dt>
                <dd className="font-mono">{issued.name}</dd>
                <dt>Prefix</dt>
                <dd className="font-mono">{issued.prefix}</dd>
                <dt>Issued</dt>
                <dd>{formatTimestamp(issued.created_at)}</dd>
              </dl>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button onClick={handleClose}>I have saved the token</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({id, label, children}: {id: string; label: string; children: ReactNode}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

function extractError(err: unknown): string {
  return (err as {detail?: string} | undefined)?.detail ?? "Request failed";
}
